import { type UseMutationResponse } from '@urql/vue';
import { useUpdateUserPreferencesMutation } from '~/composables/__generated__';
import { MeDocument } from '~/graphql';
import { AvailableExternalContentIntegrations, AvailableTimetableIntegrations } from '~/utils/constants';
import { Permission } from '@teachino/shared-util';

export enum UserPreferenceKeys {
  TIMETABLE_WEEKDAYS = 'timetable_weekdays',
}

export enum TimetableWeekdaysValues {
  FIVE_DAYS = 'five_days',
  SEVEN_DAYS = 'seven_days',
}

export default function () {
  const me = useState<{}>('me');
  const userIsLoaded = useState<boolean>('userIsLoaded', () => false);
  let updateUserPreferencesMutation: UseMutationResponse<any>;

  function setup() {
    updateUserPreferencesMutation = useUpdateUserPreferencesMutation();
  }

  function getToken(): string | null {
    return localStorage.getItem('token');
  }

  async function loadUserIfNeeded() {
    if (!userIsLoaded.value) {
      const { $urql, $t } = useNuxtApp();
      const { data, error } = await $urql.executeQuery({
        query: MeDocument,
      });
      if (error) {
        throw new Error($t('generic_error'));
      }
      me.value = data;
      userIsLoaded.value = true;
    }
  }

  async function reloadUser() {
    const { $urql, $t } = useNuxtApp();
    const { data, error } = await $urql.executeQuery({
      query: MeDocument,
    });
    if (error) {
      throw new Error($t('error_generic'));
    }
    me.value = data;
  }

  function getMeRef() {
    return me;
  }

  function hasActiveTimetableIntegration() {
    return (
      me.value.externalIntegrations.find(
        (integration) =>
          AvailableTimetableIntegrations.indexOf(
            integration.externalIntegrationSetting.key,
          ) > -1,
      ) !== undefined
    );
  }

  function hasActiveExternalContentIntegration() {
    return (
      me.value.externalIntegrations.find(
        (integration) =>
          AvailableExternalContentIntegrations.indexOf(
            integration.externalIntegrationSetting.key,
          ) > -1,
      ) !== undefined
    );
  }

  function hasActiveExternalClassNoteIntegration() {
    //TODO: refactor to use backend data with TT-828
    return (
      me.value.externalIntegrations.find(
        (integration) =>
          AvailableExternalContentIntegrations.indexOf(
            integration.externalIntegrationSetting.key,
          ) > -1,
      ) !== undefined
    );
  }

  function getActiveExternalContentIntegration() {
    return me.value.externalIntegrations.find(
      (integration) =>
        AvailableExternalContentIntegrations.indexOf(
          integration.externalIntegrationSetting.key,
        ) > -1,
    );
  }

  function getUserId() {
    return me.value.me.id;
  }

  function getFullName(): string {
    if (!me.value?.me) {
      return '';
    }
    return `${me.value.me.firstName  } ${  me.value.me.lastName}`;
  }

  function getFirstName(): string {
    if (!me.value?.me) {
      return '';
    }
    return me.value.me.firstName;
  }

  function getDefaultTentantId() {
    return me.value?.me?.defaultTenant?.id || '';
  }

  function getPreferences() {
    const preferences = me.value.config.featureFlags.find(
      (flag) => flag.key === 'preferences',
    );
    if (preferences) {
      return preferences.settings;
    }
    return {};
  }

  function getPreference(key: UserPreferenceKeys) {
    return getPreferences()[key];
  }

  async function updatePreferences(key: UserPreferenceKeys, value: any) {
    await updateUserPreferencesMutation.executeMutation({
      updateUserPreferencesInput: {
        preferences: {
          ...getPreferences(),
          [key]: value,
        },
      },
    });
  }

  function isTeachinoUser() {
    if (!me.value?.me?.email) {
      return false;
    }
    return me.value.me.email.indexOf('@teachino.io') > 0;
  }

  function hasPermission(permission: Permission) {
    return me.value.currentLicense.plan.features.some(
      (feature) => feature.key === permission,
    );
  }

  return {
    getToken,
    getPreference,
    getPreferences,
    getDefaultTentantId,
    updatePreferences,
    getUserId,
    loadUserIfNeeded,
    reloadUser,
    getMeRef,
    getFullName,
    getFirstName,
    hasActiveTimetableIntegration,
    hasActiveExternalContentIntegration,
    hasActiveExternalClassNoteIntegration,
    getActiveExternalContentIntegration,
    setup,
    hasPermission
  };
}
